import React from 'react';
import { Eye, Loader2, MapPin, Activity, Globe, Clock } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ACTION_CONFIG, formatRelativeTime, formatFullDate } from './activityUtils';
import { LocationText } from './LocationText';

export const UserDetailDialog = ({ open, onOpenChange, loading, userDetail }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-2xl bg-gray-900 border-gray-700 max-h-[85vh] overflow-y-auto" data-testid="user-detail-dialog">
      <DialogHeader>
        <DialogTitle className="text-white flex items-center gap-3">
          <Eye size={20} className="text-cyan-400" />
          Detalle del Usuario
        </DialogTitle>
      </DialogHeader>
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={32} className="text-cyan-400 animate-spin" />
        </div>
      ) : userDetail ? (
        <div className="space-y-6 mt-2">
          <div className="flex items-center gap-4 p-4 rounded-xl bg-gray-800/50 border border-gray-700/50">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-cyan-500/30 to-blue-600/30 border border-cyan-500/40 flex items-center justify-center">
              <span className="text-white font-bold text-xl">
                {userDetail.user?.name?.charAt(0)?.toUpperCase() || '?'}
              </span>
            </div>
            <div className="flex-1">
              <p className="text-white font-semibold text-lg">{userDetail.user?.name}</p>
              <p className="text-gray-500 text-sm">{userDetail.user?.email}</p>
            </div>
            <div className="text-right">
              <p className="text-gray-400 text-xs">Ultimo acceso</p>
              <p className="text-white text-sm">{formatRelativeTime(userDetail.user?.last_active)}</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-center">
              <p className="text-2xl font-bold text-blue-400">{userDetail.stats?.logins || 0}</p>
              <p className="text-gray-500 text-xs">Sesiones</p>
            </div>
            <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20 text-center">
              <p className="text-2xl font-bold text-purple-400">{userDetail.stats?.predictions || 0}</p>
              <p className="text-gray-500 text-xs">Predicciones</p>
            </div>
            <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 text-center">
              <p className="text-2xl font-bold text-green-400">{userDetail.stats?.recommendations || 0}</p>
              <p className="text-gray-500 text-xs">Recomendaciones</p>
            </div>
          </div>
          {userDetail.locations?.length > 0 && (
            <div>
              <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                <Globe size={16} className="text-cyan-400" />
                Ubicaciones de Conexion
              </h4>
              <div className="space-y-2">
                {userDetail.locations.map((loc, idx) => (
                  <div key={`loc-${idx}`} className="flex items-center justify-between p-3 rounded-lg bg-gray-800/50 border border-gray-700/30">
                    <div className="flex items-center gap-3">
                      <MapPin size={14} className="text-red-400" />
                      <div>
                        <p className="text-gray-300 text-sm">
                          {loc.location?.city || '-'}{loc.location?.region ? `, ${loc.location.region}` : ''} ({loc.location?.country})
                        </p>
                        <p className="text-gray-600 text-[11px]">IP: {loc.ip_address}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="text-gray-400 border-gray-600 text-[10px]">
                        {loc.count} {loc.count === 1 ? 'vez' : 'veces'}
                      </Badge>
                      <p className="text-gray-600 text-[10px] mt-0.5">{formatRelativeTime(loc.last_seen)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div>
            <h4 className="text-white font-medium mb-3 flex items-center gap-2">
              <Activity size={16} className="text-cyan-400" />
              Actividad Reciente
            </h4>
            <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
              {userDetail.activities?.slice(0, 30).map((a) => {
                const config = ACTION_CONFIG[a.action] || ACTION_CONFIG.login;
                const Icon = config.icon;
                return (
                  <div key={a.id} className="flex items-center gap-3 p-3 rounded-lg bg-gray-800/30 border border-gray-700/20">
                    <div className={`w-8 h-8 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0`}>
                      <Icon size={14} className={config.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-300 text-sm">{config.label}</p>
                      {a.details?.lottery_name && <p className="text-gray-500 text-xs">{a.details.lottery_name}</p>}
                      {a.details?.numbers && (
                        <div className="flex gap-1 mt-0.5">
                          {a.details.numbers.map((n, i) => (
                            <span key={`num-${i}`} className="w-5 h-5 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center text-[9px] font-bold">{n}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-gray-500 text-[11px]">{formatFullDate(a.created_at)}</p>
                      <LocationText location={a.location} />
                    </div>
                  </div>
                );
              })}
              {(!userDetail.activities || userDetail.activities.length === 0) && (
                <p className="text-gray-500 text-center py-6">Sin actividad registrada</p>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </DialogContent>
  </Dialog>
);
